import type { LabConfig, TestResult } from '../types';
import { computeStats, measureFixedWork, now } from '../utils';

const GROUP = 'H_other';

type AudioRenderResult = {
  durationMs: number;
  sampleRate: number;
  bufferSize: number;
  hash: number;
  sum: number;
  absSum: number;
  rms: number;
};

function hashAudio(data: Float32Array): Pick<AudioRenderResult, 'hash' | 'sum' | 'absSum' | 'rms'> {
  let hash = 2166136261 >>> 0;
  let sum = 0;
  let absSum = 0;
  let squares = 0;
  let count = 0;

  // Sample every 16th value. Stable, fast, and enough for implementation fingerprinting.
  for (let i = 0; i < data.length; i += 16) {
    const value = data[i] || 0;
    sum += value;
    absSum += Math.abs(value);
    squares += value * value;
    count++;
    const quantized = Math.max(-2147483648, Math.min(2147483647, Math.round(value * 1e7))) | 0;
    hash ^= quantized;
    hash = Math.imul(hash, 16777619) >>> 0;
  }

  return { hash, sum, absSum, rms: count > 0 ? Math.sqrt(squares / count) : 0 };
}

async function renderOfflineAudio(
  name: string,
  buildGraph: (ctx: OfflineAudioContext) => void,
  length: number,
  sampleRate: number
): Promise<AudioRenderResult> {
  const OfflineAudio = window.OfflineAudioContext || (window as any).webkitOfflineAudioContext;
  if (!OfflineAudio) throw new Error('OfflineAudioContext is not available');

  const ctx = new OfflineAudio(1, length, sampleRate) as OfflineAudioContext;
  buildGraph(ctx);
  const t0 = now();
  const buffer = await ctx.startRendering();
  const durationMs = now() - t0;
  const metrics = hashAudio(buffer.getChannelData(0));
  return { durationMs, sampleRate: ctx.sampleRate, bufferSize: ctx.length, ...metrics };
}

function buildClassicAudioGraph(ctx: OfflineAudioContext): void {
  const osc = ctx.createOscillator();
  osc.type = 'triangle';
  osc.frequency.value = 10_000;

  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = -50;
  compressor.knee.value = 40;
  compressor.ratio.value = 12;
  compressor.attack.value = 0;
  compressor.release.value = 0.25;

  osc.connect(compressor);
  compressor.connect(ctx.destination);
  osc.start(0);
}

function buildBiquadCascadeGraph(ctx: OfflineAudioContext): void {
  const length = ctx.length;
  const source = ctx.createBufferSource();
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let seed = 123456789;
  for (let i = 0; i < length; i++) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    data[i] = (seed / 0xffffffff) * 2 - 1;
  }
  source.buffer = buffer;

  let node: AudioNode = source;
  for (let i = 0; i < 6; i++) {
    const filter = ctx.createBiquadFilter();
    filter.type = i % 2 ? 'highpass' : 'lowpass';
    filter.frequency.value = 300 + i * 900;
    filter.Q.value = 0.7 + i * 0.2;
    node.connect(filter);
    node = filter;
  }
  node.connect(ctx.destination);
  source.start(0);
}

function buildConvolverGraph(ctx: OfflineAudioContext): void {
  const length = ctx.length;
  const source = ctx.createBufferSource();
  const sourceBuffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const sourceData = sourceBuffer.getChannelData(0);
  for (let i = 0; i < length; i++) {
    sourceData[i] = Math.sin(i * 0.013) * Math.exp((-i / length) * 4);
  }
  source.buffer = sourceBuffer;

  const convolver = ctx.createConvolver();
  const impulse = ctx.createBuffer(1, 1024, ctx.sampleRate);
  const impulseData = impulse.getChannelData(0);
  for (let i = 0; i < impulseData.length; i++) {
    impulseData[i] = (Math.sin(i * 0.07) + Math.cos(i * 0.031)) * Math.exp(-i / 400);
  }
  convolver.buffer = impulse;

  source.connect(convolver);
  convolver.connect(ctx.destination);
  source.start(0);
}

function trimExtremes(samples: number[]): number[] {
  if (samples.length < 5) return samples;
  const sorted = [...samples].sort((a, b) => a - b);
  return sorted.slice(1, -1);
}

async function measureAudioRender(
  name: string,
  buildGraph: (ctx: OfflineAudioContext) => void,
  options: { length: number; sampleRate: number; runs: number; warmup: number; batch: number }
): Promise<TestResult> {
  const { length, sampleRate, runs, warmup, batch } = options;
  for (let i = 0; i < warmup; i++) {
    for (let j = 0; j < batch; j++) await renderOfflineAudio(name, buildGraph, length, sampleRate);
  }

  const samples: number[] = [];
  let first: AudioRenderResult | null = null;
  const hashes = new Set<number>();
  const wallStart = now();
  for (let i = 0; i < runs; i++) {
    const batchStart = now();
    for (let j = 0; j < batch; j++) {
      const result = await renderOfflineAudio(name, buildGraph, length, sampleRate);
      if (!first) first = result;
      hashes.add(result.hash);
    }
    samples.push((now() - batchStart) / batch);
  }

  const trimmedSamples = trimExtremes(samples);

  return {
    name,
    group: GROUP,
    success: true,
    durationMs: now() - wallStart,
    samples,
    stats: computeStats(trimmedSamples),
    metadata: {
      sampleRate,
      bufferSize: length,
      batch,
      stableHash: hashes.size === 1,
      rawSamples: samples,
      trimmedSamples,
      hashes: [...hashes],
      first,
    },
  };
}

function runCanvasTextRender(): { durationMs: number; pixelHash: number; width: number; height: number } {
  const width = 300;
  const height = 100;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  const t0 = now();
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, width, height);
  ctx.fillStyle = '#000';
  ctx.font = '24px Arial';
  ctx.fillText('Hello, hardware!', 10, 50);
  ctx.font = '16px serif';
  ctx.fillText('The quick brown fox', 10, 80);
  const t1 = now();

  const data = ctx.getImageData(0, 0, width, height).data;
  let hash = 0;
  for (let i = 0; i < data.length; i += 4) {
    hash = (hash * 31 + data[i] + data[i + 1] + data[i + 2]) >>> 0;
  }

  return { durationMs: t1 - t0, pixelHash: hash, width, height };
}

function runCanvasFilterRender(): number {
  const width = 256;
  const height = 256;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  const img = ctx.createImageData(width, height);
  let seed = 123456789;
  for (let i = 0; i < img.data.length; i += 4) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    img.data[i] = seed % 256;
    seed = (seed * 1664525 + 1013904223) >>> 0;
    img.data[i + 1] = seed % 256;
    seed = (seed * 1664525 + 1013904223) >>> 0;
    img.data[i + 2] = seed % 256;
    img.data[i + 3] = 255;
  }

  const t0 = now();
  ctx.putImageData(img, 0, 0);
  ctx.filter = 'blur(10px) contrast(150%) brightness(1.2)';
  ctx.drawImage(canvas, 0, 0);
  return now() - t0;
}

export async function runOtherTests(config: LabConfig): Promise<TestResult[]> {
  const t0 = now();
  const tests: TestResult[] = [];

  if (config.mode !== 'prod') {
    if (!config.enableAudio) {
      tests.push({ name: 'audio_disabled', group: GROUP, success: false, durationMs: 0, error: 'Audio tests disabled in config' });
    } else {
      const audioOptions = { runs: 5, warmup: 2 };
      const audioTests = [
        ['offline_audio_classic', buildClassicAudioGraph, 8000, 44100, 5] as const,
        ['offline_audio_biquad', buildBiquadCascadeGraph, 32768, 48000, 4] as const,
        ['offline_audio_convolver', buildConvolverGraph, 16384, 48000, 4] as const,
      ];

      for (const [name, graph, length, sampleRate, batch] of audioTests) {
        try {
          tests.push(await measureAudioRender(name, graph, { length, sampleRate, batch, ...audioOptions }));
        } catch (err) {
          tests.push({ name, group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
        }
      }
    }
  }

  try {
    const result = runCanvasTextRender();
    tests.push({
      name: 'canvas_text_render',
      group: GROUP,
      success: true,
      durationMs: result.durationMs,
      metadata: { pixelHash: result.pixelHash, width: result.width, height: result.height },
    });
  } catch (err) {
    tests.push({ name: 'canvas_text_render', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
  }

  try {
    const samples = measureFixedWork(() => runCanvasFilterRender(), { runs: config.mode === 'prod' ? 3 : 10, warmup: 1 });
    tests.push({ name: 'canvas_filter_render', group: GROUP, success: true, durationMs: samples.reduce((a, b) => a + b, 0), samples, stats: computeStats(samples) });
  } catch (err) {
    tests.push({ name: 'canvas_filter_render', group: GROUP, success: false, durationMs: now() - t0, error: err instanceof Error ? err.message : String(err) });
  }

  return tests;
}
